// 生词本列表组件
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  List,
  Button,
  message,
  Select,
  Pagination,
  Popconfirm,
  Empty,
  Spin,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  BookOutlined,
  CalendarOutlined,
  NumberOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import styles from '@/assets/styles/VocabularyList.module.less';
import type { components, paths } from '@/api/types';
import { getVocabularyLists, deleteVocabularyList } from '@/libs/api';
import CreateVocabularyModal from './CreateVocabularyModal';
import EditVocabularyModal from './EditVocabularyModal';

type VocabularyListDto = components['schemas']['VocabularyListDto'];
type VocabularyListParams = paths['/vocabulary/lists']['get']['parameters']['query'];
type VocabularyListOrderBy = NonNullable<VocabularyListParams>['orderBy'];
type OrderDirection = NonNullable<VocabularyListParams>['direction'];

const { Option } = Select;

const DEFAULT_THEME_COLOR = '#6a0dad';

const VocabularyList: React.FC<
  {
    userId: string;
    userName: string;
  }> = ({ userId, userName }) => {
  const navigate = useNavigate();
  const [vocabularyBooks, setVocabularyBooks] = useState<VocabularyListDto[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingBook, setEditingBook] = useState<VocabularyListDto | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [orderBy, setOrderBy] = useState<VocabularyListOrderBy>('createdAt');
  const [direction, setDirection] = useState<OrderDirection>('DESC');
  const [loading, setLoading] = useState(false);

  const fetchVocabularyLists = async () => {
    setLoading( true );
    console.log(userId, userName);
    try {
      const response = await getVocabularyLists({
        page: currentPage,
        limit: pageSize,
        orderBy,
        direction,
      });
      setVocabularyBooks(response.lists);
      setTotal(response.total);
    } catch (error) {
      message.error('获取生词本列表失败');
      console.error('Fetch vocabulary lists error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVocabularyLists();
  }, [currentPage, pageSize, orderBy, direction]);

  const handleCreateSuccess = () => {
    fetchVocabularyLists();
  };

  const handleEditSuccess = () => {
    fetchVocabularyLists();
  };

  const handleDeleteBook = async (id: number) => {
    try {
      await deleteVocabularyList(id);
      message.success('删除成功');
      fetchVocabularyLists();
    } catch (error) {
      message.error('删除生词本失败');
      console.error('Delete vocabulary list error:', error);
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>, bookId: number) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.ant-card-actions')) {
      navigate(`/vocabulary/list/${bookId}`);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={'notDisplayInMobile'}>
          <BookOutlined /> 我的生词本
        </h2>
        <div className={styles.headerControls}>
          <Select
            value={orderBy}
            onChange={setOrderBy}
            style={{ width: 120, marginRight: 8 }}
            className={'notDisplayInMobile'}
          >
            <Option value="createdAt">创建时间</Option>
            <Option value="name">名称</Option>
            <Option value="wordCount">单词数量</Option>
          </Select>
          <Select
            value={direction}
            onChange={setDirection}
            style={{ width: 100, marginRight: 16 }}
            className={'notDisplayInMobile'}
          >
            <Option value="ASC">升序</Option>
            <Option value="DESC">降序</Option>
          </Select>
          <Button
            className={'notDisplayInMobile'}
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            新建生词本
          </Button>
        </div>
      </div>
<br/>
      <Spin spinning={loading}>
        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 2,
            md: 2,
            lg: 3,
            xl: 3,
            xxl: 4,
          }}
          dataSource={vocabularyBooks}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="还没有生词本，快来创建一个吧！"
              >
                <Button type="primary" onClick={() => setIsModalVisible(true)}>
                  创建生词本
                </Button>
              </Empty>
            ),
          }}
          renderItem={book => (
            <List.Item>
              <Card
                className={styles.bookCard}
                onClick={e => handleCardClick(e, book.id)}
                style={{ cursor: 'pointer' }}
                actions={[
                  <EditOutlined
                    key="edit"
                    onClick={() => {
                      setEditingBook(book);
                      setIsEditModalVisible(true);
                    }}
                  />,
                  <Popconfirm
                    key="delete"
                    title="确定要删除这个生词本吗？"
                    onConfirm={() => handleDeleteBook(book.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <DeleteOutlined key="delete" className={styles.deleteIcon} />
                  </Popconfirm>,
                ]}
              >
                <div className={styles.cardHeader}>
                  <h5>
                    <BookOutlined style={{ color: book.themeColor || DEFAULT_THEME_COLOR }} />{' '}
                    {book.name}
                  </h5>
                  <Tag
                    color={book.isPublic ? '#6a0dad' : 'default'}
                    icon={book.isPublic ? <GlobalOutlined /> : <LockOutlined />}
                  >
                    {book.isPublic ? '公开' : '私有'}
                  </Tag>
                </div>
                <p>
                  <NumberOutlined /> 单词数量: {book.wordCount}
                </p>
                <p>
                  <CalendarOutlined /> 创建时间: {new Date(book.createdAt).toLocaleDateString()}
                </p>
                {book.tags && book.tags.length > 0 && (
                  <div className={styles.tags}>
                    {book.tags.map((tag, index) => (
                      <Tag key={index} color="#eee1f8">
                        {tag}
                      </Tag>
                    ))}
                  </div>
                )}
              </Card>
            </List.Item>
          )}
        />
      </Spin>

      {total > 0 && (
        <div className={styles.pagination}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={total}
            onChange={(page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            }}
            showSizeChanger
            showTotal={total => `共 ${total} 个生词本`}
          />
        </div>
      )}

      <CreateVocabularyModal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditVocabularyModal
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingBook(null);
        }}
        onSuccess={handleEditSuccess}
        book={editingBook}
      />
    </div>
  );
};

export default VocabularyList;
